import React from "react";
import { toast } from "sonner";
import { Loader2, PackageCheck, X, ChevronDown, ChevronRight, RotateCcw, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * PF2 · Partial Pickup Admin Workflow
 * ─────────────────────────────────────────────────────────────
 * Presents a Record Pickup modal (allocation panel) + inline
 * Fulfillment History strip. Both talk to the PF2 endpoints
 * on `/api/admin/orders/{public_id}/fulfillments`.
 *
 * The parent (`FulfillmentAdmin.jsx`) supplies:
 *   - `order`            the currently loaded order document
 *   - `onDone()`         invoked after a successful mutation so the
 *                        parent reloads the order
 *   - `data`             (optional) prefetched GET /fulfillments payload
 *
 * All interactive elements carry deterministic `data-testid`s so
 * Playwright + Jest can drive the flow.
 */

const asPositiveInt = (v) => {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * Load the fulfillment history + totals for an order. Returns the
 * server payload verbatim (fulfillments[], totals, fulfillment_summary).
 * The hook exposes a `refresh` handle for after-mutation reloads.
 */
export function useFulfillments(publicId) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const refresh = React.useCallback(async () => {
    if (!publicId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/orders/${publicId}/fulfillments`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Failed to load history");
    } finally { setLoading(false); }
  }, [publicId]);

  React.useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, error, refresh };
}


/**
 * Compute per-code capacity for the Record Pickup panel:
 *
 *   qty_picked           (from fulfillment_items[i].qty_picked)
 *   already_fulfilled    (Σ non-voided allocations across events)
 *   available_now        max(0, qty_picked - already_fulfilled)
 *
 * Only rows with `available_now > 0` are shown — items still pending
 * or already fully picked up don't clutter the panel.
 */
function buildAllocationRows(order, fulfilledByCode) {
  const rows = [];
  for (const it of (order?.fulfillment_items || [])) {
    if (it.status === "OutOfStock" || it.status === "Removed") continue;
    const picked = Number(it.qty_picked || 0);
    const already = Number((fulfilledByCode || {})[String(it.code).toUpperCase()] || 0);
    const available = Math.max(0, picked - already);
    if (available <= 0) continue;
    rows.push({
      code: String(it.code || "").toUpperCase(),
      country: it.country || "",
      qty_picked: picked,
      already_fulfilled: already,
      available_now: available,
      selected: true,
      qty_now: available,
    });
  }
  // Deterministic sort — code alphabetical for stable UI + tests.
  rows.sort((a, b) => a.code.localeCompare(b.code));
  return rows;
}


export function RecordPickupModal({ order, onClose, onSuccess, fulfilledByCode, mode = "pickup" }) {
  const isShipping = mode === "shipping";
  const [rows, setRows] = React.useState(() => buildAllocationRows(order, fulfilledByCode));
  const [notes, setNotes] = React.useState("");
  const [carrier, setCarrier] = React.useState("UPS Ground");
  const [tracking, setTracking] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const idempotencyKey = React.useMemo(
    () => `pf2-${order.public_id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    [order.public_id],
  );

  const noTrackingCarrier = String(carrier || "").toLowerCase() === "usps stamp";
  const trackingRequired = isShipping && !noTrackingCarrier;
  const carrierMissing = isShipping && !String(carrier || "").trim();
  const trackingMissing = trackingRequired && !String(tracking || "").trim();

  const pickupNow = rows
    .filter((r) => r.selected)
    .reduce((s, r) => s + asPositiveInt(r.qty_now), 0);

  const readyNotPickedUp = rows.reduce(
    (s, r) => s + Math.max(0, r.available_now - (r.selected ? asPositiveInt(r.qty_now) : 0)),
    0,
  );

  const stillPending = Number(order?.fulfillment_summary?.pending_total || 0);

  const setRow = (code, patch) =>
    setRows((prev) => prev.map((r) => (r.code === code ? { ...r, ...patch } : r)));

  const canConfirm = pickupNow > 0 && !submitting
    && !carrierMissing && !trackingMissing;

  const noun = isShipping ? "sticker" : "sticker";
  const verbTitle = isShipping ? "Create Shipment" : "Record Pickup";
  const primaryLabel = isShipping ? "Confirm Shipment" : "Confirm Pickup";
  const nowLabel = isShipping ? "Shipping now" : "Picking up now";
  const readyLabel = isShipping ? "Ready but not shipping now" : "Ready but not picked up";

  const onConfirm = async () => {
    const items = rows
      .filter((r) => r.selected && asPositiveInt(r.qty_now) > 0)
      .map((r) => ({ code: r.code, qty: asPositiveInt(r.qty_now) }));
    if (items.length === 0) {
      toast.error(`Select at least one sticker to ${isShipping ? "ship" : "record"}.`);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        method: isShipping ? "shipping" : "pickup",
        items,
        notes: notes.trim() || undefined,
        idempotency_key: idempotencyKey,
      };
      if (isShipping) {
        body.carrier = String(carrier || "").trim();
        body.tracking_number = String(tracking || "").trim() || undefined;
      }
      const r = await api.post(
        `/admin/orders/${order.public_id}/fulfillments`, body,
      );
      const remainingPending = Number(r.data?.fulfillment_summary?.pending_total || 0);
      const readyNot = Number(r.data?.totals?.ready_not_fulfilled_total || 0);
      const terminal = !!r.data?.terminally_complete;
      // PF5 · Fulfillment-Event-Scoped Customer Email outcome. Accurate
      // neutral copy — never claims "will retry" (there is no auto-retry).
      const email = r.data?.customer_email || {};
      const emailStatus = String(email.status || "").toLowerCase();
      let emailTail = "";
      if (emailStatus === "sent") {
        emailTail = " Customer email sent.";
      } else if (emailStatus === "skipped_already_sent") {
        emailTail = "";
      } else if (emailStatus === "no_recipient") {
        emailTail = " Customer email could not be sent (no email on file).";
      } else if (emailStatus === "not_configured") {
        emailTail = " Customer email could not be sent (provider not configured).";
      } else if (emailStatus === "failed" || emailStatus === "error") {
        emailTail = " Customer email could not be sent. Fulfillment was still recorded.";
      }
      const baseSuccess = terminal
        ? (isShipping ? "Final shipment created — order complete!" : "Order fully picked up — complete!")
        : `${isShipping ? "Shipment" : "Pickup"} recorded: ${pickupNow} ${noun}${pickupNow === 1 ? "" : "s"}.`
            + (remainingPending + readyNot > 0
                ? ` ${remainingPending + readyNot} still pending.`
                : "");
      toast.success(baseSuccess + emailTail);
      onSuccess({
        total: r.data?.totals?.fulfilled_total ?? pickupNow,
        terminal,
        remaining_pending: remainingPending,
        ready_not_picked_up: readyNot,
        customer_email: email,
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || `${verbTitle} failed.`);
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-2 sm:p-6"
      data-testid={isShipping ? "create-shipment-modal" : "record-pickup-modal"}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">{verbTitle}</div>
            <div className="text-lg font-black text-slate-900">{order.public_id}</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full"
            data-testid="record-pickup-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4" data-testid="record-pickup-body">
          {rows.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center" data-testid="record-pickup-empty">
              No stickers are currently ready to {isShipping ? "ship" : "pick up"}.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-50 border-b">
                <span className="w-6" />
                <span>Sticker</span>
                <span className="text-right">Available</span>
                <span className="text-right pl-2">{isShipping ? "Ship now" : "Pickup now"}</span>
              </div>
              {rows.map((r) => (
                <div
                  key={r.code}
                  className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center px-3 py-2 border-b last:border-b-0 text-sm"
                  data-testid={`record-pickup-row-${r.code}`}
                >
                  <input
                    type="checkbox"
                    checked={r.selected}
                    onChange={(e) => setRow(r.code, {
                      selected: e.target.checked,
                      qty_now: e.target.checked ? r.available_now : 0,
                    })}
                    data-testid={`record-pickup-select-${r.code}`}
                    aria-label={`Include ${r.code}`}
                  />
                  <div className="min-w-0">
                    <div className="font-mono font-semibold text-slate-900">{r.code}</div>
                    <div className="text-[11px] text-slate-500">
                      {r.country ? `${r.country} · ` : ""}
                      Ready {r.qty_picked}
                      {r.already_fulfilled > 0 && ` · Already fulfilled ${r.already_fulfilled}`}
                    </div>
                  </div>
                  <div className="text-right text-slate-600" data-testid={`record-pickup-available-${r.code}`}>
                    {r.available_now}
                  </div>
                  <input
                    type="number"
                    min={0}
                    max={r.available_now}
                    step={1}
                    value={r.selected ? r.qty_now : 0}
                    disabled={!r.selected}
                    onChange={(e) => setRow(r.code, {
                      qty_now: Math.min(r.available_now, Math.max(0, asPositiveInt(e.target.value))),
                    })}
                    className="w-16 border border-slate-300 rounded px-2 py-1 text-right font-mono text-sm disabled:bg-slate-100"
                    data-testid={`record-pickup-qty-${r.code}`}
                    aria-label={`Quantity for ${r.code}`}
                  />
                </div>
              ))}
            </div>
          )}

          {isShipping && rows.length > 0 && (
            <div className="space-y-2" data-testid="create-shipment-metadata">
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                  Carrier
                </label>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl p-2 text-sm bg-white"
                  data-testid="create-shipment-carrier"
                >
                  <option value="UPS Ground">UPS Ground</option>
                  <option value="USPS Stamp">USPS Stamp (no tracking)</option>
                  <option value="USPS Priority">USPS Priority</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                  Tracking number {noTrackingCarrier && <span className="text-slate-400 normal-case">(optional for {carrier})</span>}
                </label>
                <input
                  type="text"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value.slice(0, 128))}
                  placeholder={noTrackingCarrier ? "No tracking" : "e.g. 1Z999AA10123456784"}
                  disabled={noTrackingCarrier}
                  className="w-full border border-slate-300 rounded-xl p-2 text-sm font-mono disabled:bg-slate-100"
                  data-testid="create-shipment-tracking"
                />
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div>
              <label className="block text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                placeholder={isShipping ? "e.g. first partial shipment" : "e.g. customer picked up available stickers"}
                rows={2}
                className="w-full border border-slate-300 rounded-xl p-2 text-sm"
                data-testid="record-pickup-notes"
              />
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="border-t bg-slate-50 px-4 sm:px-5 py-3 space-y-2" data-testid="record-pickup-summary">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-700">{nowLabel}</span>
              <span className="font-mono font-bold text-slate-900" data-testid="record-pickup-summary-now">
                {pickupNow} sticker{pickupNow === 1 ? "" : "s"}
              </span>
            </div>
            {readyNotPickedUp > 0 && (
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>{readyLabel}</span>
                <span className="font-mono" data-testid="record-pickup-summary-ready-left">
                  {readyNotPickedUp}
                </span>
              </div>
            )}
            {stillPending > 0 && (
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>Still pending / not ready</span>
                <span className="font-mono" data-testid="record-pickup-summary-pending">
                  {stillPending}
                </span>
              </div>
            )}
            {(carrierMissing || trackingMissing) && (
              <div className="text-xs text-red-600" data-testid="create-shipment-validation-hint">
                {carrierMissing ? "Carrier is required." : `Tracking number required for ${carrier}.`}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={onClose}
                className="btn-ghost text-sm flex-1"
                data-testid="record-pickup-cancel"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={!canConfirm}
                className="btn-red text-sm flex-1"
                data-testid="record-pickup-confirm"
              >
                {submitting
                  ? (<><Loader2 size={14} className="animate-spin" /> Recording…</>)
                  : (<><PackageCheck size={14} /> {primaryLabel}</>)}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Fulfillment History strip — chronological list of past events. Void
 * happens via the same api.post call the tests hit. Legacy orders with
 * no events but a completed status are rendered as a single derived
 * "Legacy Pickup / Legacy Shipment" row for continuity.
 */
export function FulfillmentHistory({ order, data, onChanged }) {
  const [expanded, setExpanded] = React.useState({});
  const events = data?.fulfillments || [];

  const legacyPickup =
    events.length === 0
      && (order?.status === "completed")
      && ((order?.delivery?.code || "").toLowerCase() === "local_pickup");
  const legacyShipment =
    events.length === 0
      && (order?.status === "shipped" || order?.status === "completed")
      && !!(order?.shipment?.tracking_number || order?.shipment?.shipped_at);

  const requestVoid = async (fid) => {
    const reason = window.prompt("Reason for voiding this pickup?");
    if (!reason || !reason.trim()) return;
    try {
      await api.post(
        `/admin/orders/${order.public_id}/fulfillments/${fid}/void`,
        { reason: reason.trim() },
      );
      toast.success("Fulfillment voided.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Void failed.");
    }
  };

  return (
    <section
      className="border border-slate-200 rounded-2xl bg-white"
      data-testid="fulfillment-history-card"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
            Fulfillment History
          </div>
          <div className="text-sm font-semibold text-slate-800">
            {data?.totals?.fulfillments_count ?? events.length} event
            {(data?.totals?.fulfillments_count ?? events.length) === 1 ? "" : "s"}
            {data?.totals?.fulfilled_total > 0 && (
              <span className="ml-2 text-slate-500 font-normal">
                · {data.totals.fulfilled_total} fulfilled
              </span>
            )}
          </div>
        </div>
        {data?.fulfillment_display && data.fulfillment_display.state !== "cancelled" && (
          <div
            className="text-right shrink-0"
            data-testid="fulfillment-display-badge"
          >
            <div
              className="text-[10px] uppercase tracking-widest font-bold text-slate-500"
              data-testid="fulfillment-display-label"
            >
              {data.fulfillment_display.label}
            </div>
            {data.fulfillment_display.summary_text && (
              <div
                className="text-xs font-semibold text-slate-800"
                data-testid="fulfillment-display-summary"
              >
                {data.fulfillment_display.summary_text}
              </div>
            )}
            {(data.fulfillment_display.ready_not_fulfilled_total > 0
              || data.fulfillment_display.pending_total > 0) && (
              <div className="text-[10px] text-slate-500" data-testid="fulfillment-display-breakdown">
                {data.fulfillment_display.ready_not_fulfilled_total > 0
                  && `Ready not handed over: ${data.fulfillment_display.ready_not_fulfilled_total}`}
                {data.fulfillment_display.ready_not_fulfilled_total > 0
                  && data.fulfillment_display.pending_total > 0 && " · "}
                {data.fulfillment_display.pending_total > 0
                  && `Still pending: ${data.fulfillment_display.pending_total}`}
              </div>
            )}
          </div>
        )}
      </header>

      {events.length === 0 && !legacyPickup && !legacyShipment && (
        <div className="text-sm text-slate-500 px-4 py-6 text-center" data-testid="fulfillment-history-empty">
          No pickups or shipments recorded yet.
        </div>
      )}

      {(legacyPickup || legacyShipment) && (
        <div
          className="px-4 py-3 text-sm border-b last:border-b-0"
          data-testid="fulfillment-history-legacy"
        >
          <div className="flex items-center gap-2 text-slate-700">
            <CheckCircle2 size={14} className="text-emerald-600" />
            <span className="font-semibold">
              {legacyShipment ? "Legacy Shipment" : "Legacy Pickup"}
            </span>
            <span className="text-slate-500">
              · Completed {fmtTime(order?.completed_at || order?.shipment?.shipped_at)}
            </span>
          </div>
          {legacyShipment && order?.shipment?.tracking_number && (
            <div className="text-xs text-slate-500 mt-1 font-mono">
              Tracking: {order.shipment.tracking_number}
            </div>
          )}
        </div>
      )}

      <ul className="divide-y">
        {events.map((ev, idx) => {
          const isVoided = ev.status === "voided";
          const label = ev.method === "pickup"
            ? `Pickup #${idx + 1}`
            : `Shipment #${idx + 1}`;
          const total = (ev.allocations || [])
            .reduce((s, a) => s + Number(a.qty || 0), 0);
          return (
            <li
              key={ev.id}
              className={`px-4 py-3 ${isVoided ? "bg-slate-50" : ""}`}
              data-testid={`fulfillment-event-${ev.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <button
                      className="hover:bg-slate-100 rounded p-0.5"
                      onClick={() => setExpanded((e) => ({ ...e, [ev.id]: !e[ev.id] }))}
                      aria-label={expanded[ev.id] ? "Collapse" : "Expand"}
                      data-testid={`fulfillment-event-toggle-${ev.id}`}
                    >
                      {expanded[ev.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <span>{label}</span>
                    {isVoided && (
                      <span className="text-[10px] uppercase tracking-widest font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                        Voided
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {fmtTime(ev.handed_over_at)}
                    {ev.created_by && ` · ${ev.created_by}`}
                    {" · "}
                    <span className="font-mono">{total} sticker{total === 1 ? "" : "s"}</span>
                    {ev.tracking_number && (
                      ev.tracking_url ? (
                        <a
                          href={ev.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 font-mono underline decoration-dotted"
                          data-testid={`fulfillment-event-tracking-${ev.id}`}
                        >
                          · Tracking {ev.tracking_number}
                        </a>
                      ) : (
                        <span
                          className="ml-2 font-mono"
                          data-testid={`fulfillment-event-tracking-${ev.id}`}
                        >
                          · Tracking {ev.tracking_number}
                        </span>
                      )
                    )}
                    {ev.method === "shipping" && !ev.tracking_number && (
                      <span className="ml-2 text-slate-400" data-testid={`fulfillment-event-no-tracking-${ev.id}`}>
                        · No Tracking
                      </span>
                    )}
                  </div>
                  {isVoided && ev.voided_reason && (
                    <div className="text-xs text-red-700 mt-1">
                      Reason: {ev.voided_reason}
                    </div>
                  )}
                  {expanded[ev.id] && (
                    <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-xs font-mono text-slate-700">
                      {(ev.allocations || []).map((a, i) => (
                        <li key={i} data-testid={`fulfillment-event-alloc-${ev.id}-${a.code}`}>
                          {a.code} × {a.qty}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {!isVoided && (
                  <button
                    onClick={() => requestVoid(ev.id)}
                    className="btn-ghost text-xs shrink-0"
                    title="Void this fulfillment"
                    data-testid={`fulfillment-event-void-${ev.id}`}
                  >
                    <RotateCcw size={12} /> Void
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
